import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Package, Plus, X } from 'lucide-react';

/**
 * Batch and serial number capture inputs shown during GRN receiving
 * for batch_tracked and serial_tracked items.
 */
export default function GrnBatchInput({ line, lineInput, onUpdateLine }) {
  const netBookingIn = Math.max(0, (parseFloat(lineInput.qty_received) || 0) - (parseFloat(lineInput.qty_rejected) || 0));
  const batches = lineInput.batches || [];
  const batchTotal = batches.reduce((s, b) => s + (parseFloat(b.quantity) || 0), 0);
  const balanced = Math.abs(batchTotal - netBookingIn) < 0.01;

  const addBatch = () => {
    const remaining = Math.max(0, netBookingIn - batchTotal);
    onUpdateLine(line.item_id, 'batches', [...batches, {
      batch_number: '', quantity: remaining || '', expiry_date: '', manufactured_date: '',
      manufacturer: '', lot_number: '', location_code: '', serial_numbers: [],
    }]);
  };

  const updateBatch = (idx, key, val) => {
    const updated = [...batches];
    updated[idx] = { ...updated[idx], [key]: val };
    if (key === 'quantity' && line.serial_tracked) {
      const qty = parseInt(val) || 0;
      const current = updated[idx].serial_numbers || [];
      if (qty > current.length) {
        updated[idx].serial_numbers = [...current, ...Array(qty - current.length).fill('')];
      } else if (qty < current.length) {
        updated[idx].serial_numbers = current.slice(0, qty);
      }
    }
    onUpdateLine(line.item_id, 'batches', updated);
  };

  const updateSerial = (batchIdx, serialIdx, val) => {
    const updated = [...batches];
    const serials = [...(updated[batchIdx].serial_numbers || [])];
    serials[serialIdx] = val;
    updated[batchIdx] = { ...updated[batchIdx], serial_numbers: serials };
    onUpdateLine(line.item_id, 'batches', updated);
  };

  const removeBatch = (idx) => {
    onUpdateLine(line.item_id, 'batches', batches.filter((_, i) => i !== idx));
  };

  return (
    <div className="mt-2 p-3 border-2 border-amber-200 bg-amber-50/50 rounded-lg" data-testid={`batch-section-${line.item_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Package className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-xs font-bold text-amber-800">
            Batch Details Required {line.serial_tracked && '(+ Serial Numbers)'}
          </span>
          {!balanced && batches.length > 0 && (
            <Badge className="bg-red-100 text-red-700 text-[9px]">
              Total: {batchTotal} / {netBookingIn} needed
            </Badge>
          )}
          {balanced && batches.length > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Balanced</Badge>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={addBatch} className="h-6 text-[10px] px-2"
          data-testid={`add-batch-${line.item_id}`}>
          <Plus className="h-3 w-3 mr-0.5" /> Add Batch
        </Button>
      </div>

      {batches.length === 0 && (
        <p className="text-[10px] text-amber-600">
          This item is batch-tracked{line.serial_tracked ? ' and serial-tracked' : ''}. Click "Add Batch" to record batch details{line.serial_tracked ? ' and serial numbers for each unit' : ''}.
        </p>
      )}

      {batches.map((b, bi) => (
        <div key={bi} className="mb-3" data-testid={`batch-row-${bi}`}>
          <div className="grid grid-cols-8 gap-1.5 items-end">
            <div className="space-y-0.5">
              <Label className="text-[9px] text-amber-700 font-semibold">Batch # *</Label>
              <Input value={b.batch_number} onChange={e => updateBatch(bi, 'batch_number', e.target.value)}
                className="h-7 text-xs" placeholder="BAT-..." data-testid={`batch-num-${bi}`} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px] text-amber-700 font-semibold">Qty *</Label>
              <Input type="number" min="0" value={b.quantity} onChange={e => updateBatch(bi, 'quantity', e.target.value)}
                className="h-7 text-xs text-right" data-testid={`batch-qty-${bi}`} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px] text-amber-700 font-semibold">Expiry Date</Label>
              <Input type="date" value={b.expiry_date} onChange={e => updateBatch(bi, 'expiry_date', e.target.value)}
                className="h-7 text-xs" data-testid={`batch-expiry-${bi}`} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px]">Mfg Date</Label>
              <Input type="date" value={b.manufactured_date} onChange={e => updateBatch(bi, 'manufactured_date', e.target.value)}
                className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px]">Manufacturer</Label>
              <Input value={b.manufacturer} onChange={e => updateBatch(bi, 'manufacturer', e.target.value)}
                className="h-7 text-xs" placeholder="Mfg..." />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px]">Lot #</Label>
              <Input value={b.lot_number} onChange={e => updateBatch(bi, 'lot_number', e.target.value)}
                className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[9px]">Location</Label>
              <Input value={b.location_code} onChange={e => updateBatch(bi, 'location_code', e.target.value)}
                className="h-7 text-xs" placeholder="WH-MAIN" />
            </div>
            <div className="flex items-end">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => removeBatch(bi)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Serial number inputs */}
          {line.serial_tracked && (parseInt(b.quantity) || 0) > 0 && (
            <div className="mt-2 ml-4 p-2 border border-blue-200 bg-blue-50/50 rounded-md" data-testid={`serials-section-${bi}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-bold text-blue-700">Serial Numbers ({(b.serial_numbers || []).filter(s => s.trim()).length}/{parseInt(b.quantity) || 0})</span>
                {(b.serial_numbers || []).filter(s => s.trim()).length === (parseInt(b.quantity) || 0) && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[8px]">Complete</Badge>
                )}
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {(b.serial_numbers || []).map((sn, si) => (
                  <Input key={si} value={sn} onChange={e => updateSerial(bi, si, e.target.value)}
                    className="h-6 text-[10px] font-mono" placeholder={`SN-${si + 1}`}
                    data-testid={`serial-input-${bi}-${si}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
